/**
 * Maps display-friendly resource type names to API endpoint paths.
 *
 * The UI shows user-friendly names (e.g., "Architect Datatable"),
 * but the API expects backend resource type identifiers that match the Genesys Cloud API.
 *
 * These mappings correspond to the resource types in ResourceTypeRegistry.cs in the backend.
 */

const resourceTypeToApiPath: Record<string, string> = {
  // Core Resources
  Group: 'group',
  Location: 'location',
  User: 'user',
  Station: 'station',
  Site: 'site',
  'Edge Group': 'edge_group',
  'Phone Base Settings': 'phone_base_settings',
  'IDP Generic': 'idp_generic',
  'IDP GSuite': 'idp_gsuite',
  'IDP Okta': 'idp_okta',
  'IDP OneLogin': 'idp_onelogin',
  'IDP Ping': 'idp_ping',
  'IDP Salesforce': 'idp_salesforce',
  'OAuth Client': 'oauth_client',

  // Architect Resources
  'Architect Flow': 'flow',
  'Architect Flow Milestone': 'flow_milestone',
  'Architect Flow Outcome': 'flow_outcome',
  'Architect Schedules': 'architect_schedules',
  'Architect Schedule Groups': 'architect_schedulegroups',
  'Architect User Prompt': 'architect_user_prompt',
  'Architect Datatable': 'architect_datatable',
  'Architect Datatable (with Data)': 'architect_datatable_with_data',
  'Architect Emergency Group': 'architect_emergencygroup',
  'Architect IVR': 'architect_ivr',
  'Architect Grammar': 'architect_grammar',
  'Architect Grammar Language': 'architect_grammar_language',

  // Routing Resources
  'Routing Queue': 'routing_queue',
  'Routing Wrapup Code': 'routing_wrapupcode',
  'Routing Skill': 'routing_skill',
  'Routing Skill Group': 'routing_skill_group',
  'Routing Language': 'routing_language',
  'Routing Email Domain': 'routing_email_domain',
  'Routing Email Route': 'routing_email_route',
  'Routing Language Skill': 'routing_language_skill',
  'Routing SMS Address': 'routing_sms_address',
  'Routing Utilization': 'routing_utilization',
  'Routing Utilization Label': 'routing_utilization_label',
  'Routing Predictor': 'routing_predictor',

  // Outbound Resources
  'Outbound Campaign': 'outbound_campaign',
  'Outbound Call Analysis Response Set': 'outbound_callanalysisresponseset',
  'Outbound Contact List': 'outbound_contact_list',
  'Outbound Contact List Filter': 'outbound_contactlistfilter',
  'Outbound DNC List': 'outbound_dnclist',
  'Outbound Attempt Limit': 'outbound_attempt_limit',
  'Outbound Callable Time Set': 'outbound_callabletimeset',
  'Outbound Campaign Rule': 'outbound_campaignrule',
  'Outbound Contact List Template': 'outbound_contact_list_template',
  'Outbound Digital Ruleset': 'outbound_digitalruleset',
  'Outbound File Specification Template': 'outbound_filespecificationtemplate',
  'Outbound Import Template': 'outbound_importtemplate',
  'Outbound Messaging Campaign': 'outbound_messagingcampaign',
  'Outbound Ruleset': 'outbound_ruleset',
  'Outbound Sequence': 'outbound_sequence',
  'Outbound Settings': 'outbound_settings',

  // Telephony Resources
  'Telephony DID Pool': 'telephony_providers_edges_did_pool',
  'Telephony DID': 'telephony_providers_edges_did',
  'Telephony Edge': 'telephony_providers_edges_edge',
  'Telephony Extension Pool': 'telephony_providers_edges_extension_pool',
  'Telephony Phone': 'telephony_providers_edges_phone',
  'Telephony Phone Base Settings': 'telephony_providers_edges_phonebasesettings',
  'Telephony Site': 'telephony_providers_edges_site',
  'Telephony Trunk Base Settings': 'telephony_providers_edges_trunkbasesettings',
  'Telephony Trunk': 'telephony_providers_edges_trunk',
  'Telephony Line Base Settings': 'telephony_providers_edges_linebasesettings',
  'Telephony Edge Group': 'telephony_providers_edges_edgegroup',
  'Telephony Certification Authority': 'telephony_providers_edges_certificationauthority',
  'Telephony Site Outbound Route': 'telephony_providers_edges_outboundroute',

  // Script/Integration Resources
  Script: 'script',
  'Widget Deployment': 'widget_deployment',
  Integration: 'integration',
  'Integration Action': 'integration_action',
  'Web Deployment Configuration': 'web_deployments_configuration',
  'Web Deployment': 'web_deployments_deployment',

  // Quality/Recording Resources
  'Quality Evaluation Form': 'quality_forms_evaluation',
  'Quality Survey Form': 'quality_forms_survey',
  'Recording Media Retention Policy': 'recording_media_retention_policy',
  'Response Management Library': 'responsemanagement_library',
  'Response Management Response': 'responsemanagement_response',
  'Response Management Response Asset': 'responsemanagement_responseasset',

  // Knowledge Resources
  'Knowledge Base': 'knowledge_knowledgebase',
  'Knowledge Document': 'knowledge_document',
  'Knowledge Document Variation': 'knowledge_document_variation',
  'Knowledge Category': 'knowledge_category',
  'Knowledge Label': 'knowledge_label',

  // Gamification Resources
  'Gamification Metric': 'gamification_metric',
  'Gamification Profile': 'gamification_profile',
  'Gamification Scorecard': 'gamification_scorecard',

  // Journey/Analytics Resources
  'Journey Action Map': 'journey_action_map',
  'Journey Action Template': 'journey_action_template',
  'Journey Segment': 'journey_segment',
  'Analytics Reporting Settings': 'analytics_reportingsettings',

  // Employee Performance
  'Employee Performance External Metrics': 'employeeperformance_externalmetrics_definition',

  // Presence Resources
  'Presence Definition': 'presence_definition',

  // Workbin Resources
  'Task Management Workbin': 'task_management_workbin',
  'Task Management Workitem Schema': 'task_management_workitem_schema',
  'Task Management Worktype': 'task_management_worktype',

  // Process Automation
  'Process Automation Trigger': 'processautomation_trigger',

  // Authorization Resources
  'Authorization Division': 'auth_division',
  'Authorization Role': 'auth_role',
  'Authorization Product': 'authorization_product',

  // Communication Resources
  'Messaging Integration Open': 'conversations_messaging_integrations_open',
  'Messaging Integration WhatsApp': 'conversations_messaging_integrations_whatsapp',
  'Messaging Settings Default': 'conversations_messaging_settings_default',
  'Messaging Settings Supported Content': 'conversations_messaging_settings_supportedcontent',

  // Compliance Resources
  'Compliance Retention Policy': 'compliance_retention_policy',

  // External Contacts
  'External Contact': 'externalcontacts_contact',
  'External Organization': 'externalcontacts_organization',

  // Group Roles
  'Group Roles': 'group_roles',

  // Platform Resources
  'Platform Edge DID Pool': 'platform_edge_did_pool',

  // Organization/System
  'Organization Authentication Settings': 'organization_authentication_settings',
  'Organization Me': 'organization_me'
};

/**
 * Converts a UI display name to the corresponding API path segment.
 * @param displayName - The user-facing resource type name
 * @returns The API path identifier, or the original name if no mapping exists
 */
export function mapResourceTypeToApiPath(displayName: string): string {
  return resourceTypeToApiPath[displayName] || displayName;
}

/**
 * Converts an API resource type to a UI display name.
 * @param apiType - The API resource type identifier (with or without "genesyscloud_" prefix)
 * @returns The display name, or a formatted version if no mapping exists
 */
export function mapApiTypeToDisplayName(apiType: string): string {
  // Remove "genesyscloud_" prefix if present
  const normalizedType = apiType.replace(/^genesyscloud_/, '');

  // Try to find in mapping
  const entry = Object.entries(resourceTypeToApiPath).find(
    ([, apiPath]) => apiPath === normalizedType
  );

  if (entry) {
    return entry[0];
  }

  // If no mapping found, format the type nicely:
  // Replace underscores with spaces and capitalize each word
  return normalizedType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Converts a UI display name to a Terraform resource type identifier.
 * @param displayName - The user-facing resource type name
 * @returns The Terraform resource type (e.g., "genesyscloud_routing_queue"),
 *          or the original name if no mapping exists
 */
export function mapDisplayNameToTerraformType(displayName: string): string {
  const apiPath = resourceTypeToApiPath[displayName];
  if (!apiPath) return displayName;

  // Convert API path to Terraform resource type by prefixing with "genesyscloud_"
  return `genesyscloud_${apiPath}`;
}

/**
 * Converts multiple display names to Terraform resource types.
 * @param displayNames - Array of user-facing resource type names
 * @returns Array of Terraform resource types
 */
export function mapDisplayNamesToTerraformTypes(displayNames: string[]): string[] {
  return displayNames.map(mapDisplayNameToTerraformType);
}
